<template>
  <section class="how-sogdo-works-section">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="section-description">{{ headerData.description }}</p>
      </div>

      <div class="row g-3">
        <div class="col-lg-8 h-auto">
          <div class="content-wrapper h-100">
            <div class="intro-content">
              <h3>{{ introData.title }}</h3>
              <p>{{ introData.description }}</p>
            </div>

            <div class="mission-content">
              <h4>{{ missionData.title }}</h4>
              <p>{{ missionData.description }}</p>
            </div>

            <div class="leadership-content">
              <h4>{{ leadershipData.title }}</h4>
              <p>{{ leadershipData.description }}</p>
            </div>

            <div class="approach-content">
              <h4>{{ approachData.title }}</h4>
              <p>{{ approachData.description }}</p>
            </div>

            <div class="policy-content">
              <h4>{{ policyData.title }}</h4>
              <p>{{ policyData.description }}</p>
            </div>
          </div>
        </div>

        <div class="col-lg-4 h-auto">
          <div class="benefits-sidebar h-100">
            <h4>{{ benefitsData.title }}</h4>
            <div class="benefits-list">
              <div class="benefit-item" v-for="(benefit, index) in benefitsData.benefits" :key="index">
                <div class="benefit-icon">
                  <i :class="benefit.icon"></i>
                </div>
                <div class="benefit-content">
                  <h5>{{ benefit.title }}</h5>
                  <p>{{ benefit.description }}</p>
                </div>
              </div>
            </div>

           
          </div>
        </div>
        <div class="col-12">
           <div class="cta-section">
              <h4>{{ ctaData.title }}</h4>
              <p>{{ ctaData.description }}</p>
              <div class="contact-info">
                <a :href="`mailto:${contactData.email}`" class="contact-link">
                  <i class="fas fa-envelope"></i>
                  {{ contactData.email }}
                </a>
              </div>
            </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WhySogdoItsHowWorksComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-cogs",
        tag: "HOW SOGDO WORKS",
        title: "Sogdo — Shaping the Future with Humanity and Technology",
        description: "Discover how we're revolutionizing the way organizations work and grow"
      })
    },
    introData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Sogdo — Shaping the Future with Humanity and Technology",
        description: "Sogdo is a forward-thinking organization driving innovation across technology, education, agriculture, AI, green solutions, and human development. Through ventures like Sogdo Synergy, SogdoTech, Sogdo Academy, and Sogdo Green, we deliver impactful solutions, services, and learning opportunities that empower people and organizations alike."
      })
    },
    missionData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Our Mission",
        description: "To improve lives by aligning technology and humanity, ensuring they work together to create real progress. We connect developers with startups, foster research, and build future-ready products that support individual growth, business success, and sustainable development."
      })
    },
    leadershipData: {
      type: Object,
      required: true,
      default: () => ({
        title: "How We Lead",
        description: "We've eliminated traditional roles like CEO, Managers, and HR in favor of a lean, efficient structure. I serve as Controller, working directly with the Co-Founder and teams to bridge strategy and execution across projects. Roles are dynamic—from entry-level to Tech Lead—based on demand. Above Tech Lead, the Controller role oversees collaboration and alignment without unnecessary hierarchy."
      })
    },
    approachData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Our Work Approach",
        description: "Using our proprietary 360° Champa Methodologies, we optimize every task for speed, quality, and efficiency—creating an agile, politics-free workplace built on trust, transparency, and innovation."
      })
    },
    policyData: {
      type: Object,
      required: true,
      default: () => ({
        title: "People-First Policy — Hiring, Not Firing",
        description: "We don't let go of team members due to temporary downtime. Instead, we support them through reskilling, R&D, or community initiatives. Every person is a valued part of the Sogdo family."
      })
    },
    benefitsData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Why Join Sogdo?",
        benefits: [
          {
            icon: "fas fa-layer-group",
            title: "Flat Hierarchy",
            description: "No politics. Your impact speaks."
          },
          {
            icon: "fas fa-balance-scale",
            title: "Flexible Culture",
            description: "Balanced and sustainable work life."
          },
          {
            icon: "fas fa-graduation-cap",
            title: "Growth & Learning",
            description: "Work on cutting-edge tech with real mentorship."
          },
          {
            icon: "fas fa-shield-alt",
            title: "Job Security",
            description: "We invest in people, not just positions."
          },
          {
            icon: "fas fa-lightbulb",
            title: "Innovation Focus",
            description: "AI, green tech, education, and more."
          },
          {
            icon: "fas fa-globe",
            title: "Real-World Impact",
            description: "Contribute to meaningful projects that improve lives."
          }
        ]
      })
    },
    ctaData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Let's Build the Future Together",
        description: "At Sogdo, you don't just work—you shape the future. Join us in creating a world where technology uplifts humanity, not replaces it."
      })
    },
    contactData: {
      type: Object,
      required: true,
      default: () => ({
        email: "say@sogdo.com"
      })
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* How Sogdo Works Section */
.how-sogdo-works-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.content-wrapper {
  background: white;
  padding: 50px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.intro-content,
.mission-content,
.leadership-content,
.approach-content,
.policy-content {
  margin-bottom: 40px;
}

.intro-content h3,
.mission-content h4,
.leadership-content h4,
.approach-content h4,
.policy-content h4 {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.intro-content p,
.mission-content p,
.leadership-content p,
.approach-content p,
.policy-content p {
  font-size: 16px;
  color: #6c757d;
  line-height: 1.7;
  margin: 0;
}

.benefits-sidebar {
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 100px;
}

.benefits-sidebar h4 {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 30px;
  font-family: 'Archivo', sans-serif;
}

.benefits-list {
  margin-bottom: 40px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 25px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 15px;
  transition: all 0.3s ease;
}

.benefit-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.benefit-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.benefit-icon i {
  font-size: 20px;
  color: white;
}

.benefit-content h5 {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-family: 'Archivo', sans-serif;
}

.benefit-content p {
  font-size: 14px;
  color: #6c757d;
  line-height: 1.5;
  margin: 0;
}

.cta-section {
  background: linear-gradient(135deg, #dc3545, #c82333);
  padding: 30px;
  border-radius: 15px;
  color: white;
  text-align: center;
}

.cta-section h4 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
  color: white;
}

.cta-section p {
  font-size: 14px;
  margin-bottom: 20px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
}

.contact-info {
  margin-top: 20px;
}

.contact-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: #dc3545;
  padding: 12px 20px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.contact-link:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 255, 255, 0.3);
  color: #dc3545;
  text-decoration: none;
}

.contact-link i {
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 991px) {
  .benefits-sidebar {
    position: static;
    margin-top: 30px;
  }
}

@media (max-width: 768px) {
  .how-sogdo-works-section {
    padding: 60px 0;
  }
  
  .section-header h2 {
    font-size: 36px;
  }
  
  .content-wrapper {
    padding: 30px 20px;
  }
  
  .benefits-sidebar {
    padding: 30px 20px;
  }
  
  .intro-content h3,
  .mission-content h4,
  .leadership-content h4,
  .approach-content h4,
  .policy-content h4 {
    font-size: 20px;
  }
  
  .benefit-item {
    padding: 15px;
  }
  
  .benefit-icon {
    width: 40px;
    height: 40px;
  }
  
  .benefit-icon i {
    font-size: 16px;
  }
}
</style>
